/**
 * Synchronous SHA-256 for the proof-of-work hot loop.
 *
 * WHY A LOCAL IMPLEMENTATION
 *
 * The PoW solver runs ~2^difficulty iterations (262,144 at
 * difficulty 18). Web Crypto's `crypto.subtle.digest` is
 * cryptographically fast but async — each call incurs
 * ~30–80µs of microtask / browser dispatch overhead regardless
 * of input size. For small inputs (challenge + 8-byte nonce)
 * the overhead eclipses the hash work by 100x and a single
 * PoW solve that should take ~100ms instead takes 10–15
 * seconds. This file is the fix: a bare-metal SHA-256
 * implemented in ~120 lines of Uint32-arithmetic that a
 * modern JIT compiles into tight loops, no async, no
 * microtask boundary per hash.
 *
 * CORRECTNESS
 *
 * FIPS 180-4 §6.2 verbatim. The algorithm constants K (cube
 * roots of the first 64 primes) and H (square roots of the
 * first 8 primes) are hard-coded; the message schedule,
 * compression function, and padding are the textbook form.
 * Tested with the three NIST FIPS 180-2 vectors and a
 * random-input differential against crypto.subtle.digest
 * (see sha256.test.ts). Any edit that changes output bytes
 * is a bug — if you're touching this file for "cleanup" or
 * "simplification", don't. Benchmark first.
 *
 * ALLOCATION POSTURE
 *
 * The PoW loop calls sha256 hundreds of thousands of times,
 * so every per-call allocation shows up. This implementation
 * allocates a fresh output ArrayBuffer per call (32 bytes —
 * cheap, and the loop consumes it read-only) but reuses
 * a preallocated message schedule and hash-state buffer
 * inside a closure returned by `makeSHA256`. Call
 * makeSHA256() once, call the returned `hash(input)` in
 * the hot loop.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const H0 = new Uint32Array([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
  0x1f83d9ab, 0x5be0cd19,
]);

// Helpers — all operate on Uint32 values (>>> 0 to coerce).
// Right-rotate is the spec's ROTR operator.
function rotr(x: number, n: number): number {
  return ((x >>> n) | (x << (32 - n))) >>> 0;
}

/**
 * Returns a SHA-256 hash function with preallocated working
 * buffers. Call once per "session" (e.g. once per PoW solve)
 * and reuse the returned `hash` in the inner loop.
 *
 * The returned function writes its output into the provided
 * `out` buffer (must be at least 32 bytes) and returns it;
 * this avoids allocating a fresh 32-byte array per hash.
 * Internal state (W, H) is cleared between calls.
 */
export function makeSHA256(): (input: Uint8Array, out: Uint8Array) => Uint8Array {
  // Message schedule W[0..63]. Reused across calls.
  const W = new Uint32Array(64);
  // Hash state a..h. Reused across calls.
  const H = new Uint32Array(8);

  return function hash(input: Uint8Array, out: Uint8Array): Uint8Array {
    if (out.length < 32) {
      throw new Error("sha256: output buffer must be at least 32 bytes");
    }
    // ── Padding ──
    // Append 0x80, pad zeros, append big-endian length in bits
    // as 64-bit value. For inputs < 56 bytes the padded
    // message fits in a single 64-byte block.
    const inputLen = input.length;
    const bitLen = inputLen * 8;
    // Number of 64-byte blocks after padding.
    const blocks = Math.ceil((inputLen + 9) / 64);
    const padded = new Uint8Array(blocks * 64);
    padded.set(input, 0);
    padded[inputLen] = 0x80;
    // Big-endian 64-bit length in the last 8 bytes of the final
    // block. JS numbers are 53-bit floats but bitLen fits
    // comfortably for any realistic PoW input, so we write the
    // high 32 bits as zero and the low 32 as bitLen.
    const lengthOffset = blocks * 64 - 8;
    // High 32 bits — always 0 for inputs smaller than 2^32 bits
    // (536 MB), vastly more than any PoW nonce could produce.
    padded[lengthOffset + 4] = (bitLen >>> 24) & 0xff;
    padded[lengthOffset + 5] = (bitLen >>> 16) & 0xff;
    padded[lengthOffset + 6] = (bitLen >>> 8) & 0xff;
    padded[lengthOffset + 7] = bitLen & 0xff;

    // ── Initial hash value ──
    H[0] = H0[0]!;
    H[1] = H0[1]!;
    H[2] = H0[2]!;
    H[3] = H0[3]!;
    H[4] = H0[4]!;
    H[5] = H0[5]!;
    H[6] = H0[6]!;
    H[7] = H0[7]!;

    // ── Process each 512-bit block ──
    for (let b = 0; b < blocks; b++) {
      const off = b * 64;

      // Prepare the message schedule W[0..63].
      for (let t = 0; t < 16; t++) {
        W[t] =
          ((padded[off + t * 4]! << 24) |
            (padded[off + t * 4 + 1]! << 16) |
            (padded[off + t * 4 + 2]! << 8) |
            padded[off + t * 4 + 3]!) >>>
          0;
      }
      for (let t = 16; t < 64; t++) {
        const w15 = W[t - 15]!;
        const w2 = W[t - 2]!;
        const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
        const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
        W[t] = (W[t - 16]! + s0 + W[t - 7]! + s1) >>> 0;
      }

      // Working variables — explicit number annotations
      // keep strict-TS happy (the `!` non-null assertions
      // otherwise confuse TS's initializer-based inference).
      let a: number = H[0]!;
      let bVar: number = H[1]!;
      let c: number = H[2]!;
      let d: number = H[3]!;
      let e: number = H[4]!;
      let f: number = H[5]!;
      let g: number = H[6]!;
      let h: number = H[7]!;

      // Compression function.
      for (let t = 0; t < 64; t++) {
        const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
        const ch = (e & f) ^ (~e & g);
        const temp1 = (h + S1 + ch + K[t]! + W[t]!) >>> 0;
        const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
        const maj = (a & bVar) ^ (a & c) ^ (bVar & c);
        const temp2 = (S0 + maj) >>> 0;

        h = g;
        g = f;
        f = e;
        e = (d + temp1) >>> 0;
        d = c;
        c = bVar;
        bVar = a;
        a = (temp1 + temp2) >>> 0;
      }

      H[0] = (H[0]! + a) >>> 0;
      H[1] = (H[1]! + bVar) >>> 0;
      H[2] = (H[2]! + c) >>> 0;
      H[3] = (H[3]! + d) >>> 0;
      H[4] = (H[4]! + e) >>> 0;
      H[5] = (H[5]! + f) >>> 0;
      H[6] = (H[6]! + g) >>> 0;
      H[7] = (H[7]! + h) >>> 0;
    }

    // ── Produce 32-byte output (big-endian) ──
    for (let i = 0; i < 8; i++) {
      const w = H[i]!;
      out[i * 4] = (w >>> 24) & 0xff;
      out[i * 4 + 1] = (w >>> 16) & 0xff;
      out[i * 4 + 2] = (w >>> 8) & 0xff;
      out[i * 4 + 3] = w & 0xff;
    }
    return out;
  };
}

/**
 * One-shot variant. Convenient for tests and single-hash
 * callers where allocation overhead doesn't matter. The PoW
 * loop uses `makeSHA256()` directly so the working buffers
 * persist across calls.
 */
export function sha256(input: Uint8Array): Uint8Array {
  const hash = makeSHA256();
  return hash(input, new Uint8Array(32));
}
